import {UiContext} from "./UiContext";
import {now, secondsElapsedSince} from "./CoreUtils";
import {IBlobStore} from "../IMemoRemote";

export function keyname(e: KeyboardEvent) {
    var keyname = e.key;
    if (e.altKey) keyname = "alt-" + keyname;
    if (e.ctrlKey) keyname = "ctrl-" + keyname;
    return keyname;
}
export function playSound(name) {
	const audio = new Audio(`sounds/${name}.mp3`);
	audio.play();
}
type MyAttrs = {
    [key: `on${any}`]: EventListener, style?: {[key:string]: string} | string, innerHTML?: string,
    appendTo?: HTMLElement | ShadowRoot, prependTo?: HTMLElement | ShadowRoot, insertBefore?: HTMLElement, insertAfter?: HTMLElement,
    [key: string]: any,
};
type UIComponent = { rootElement: HTMLElement };
export function EL<K extends keyof HTMLElementTagNameMap>(tagName: K, attrs:MyAttrs, ...children:(string|HTMLElement|UIComponent)[]): HTMLElementTagNameMap[K];
export function EL(tagName:string, attrs:MyAttrs, ...children:(string|HTMLElement|UIComponent)[]) : HTMLElement;
export function EL(tagName:string, attrs:MyAttrs, ...children:(string|HTMLElement|UIComponent)[]) : HTMLElement {
    if (attrs?.idGetOrCreate) {
        const el = document.getElementById(attrs.idGetOrCreate);
        if (el) {
            return el;
        } else {
            attrs.id = attrs.idGetOrCreate; delete attrs.idGetOrCreate;
        }
    }
	var el = document.createElement(tagName);
	if (attrs) for(var key in attrs)
		if (key === 'style' && typeof attrs[key] === 'object') Object.assign(el.style, attrs[key]);
        else if (key === 'innerHTML') el.innerHTML = attrs[key];
        else if (key === 'appendTo' && (attrs.appendTo instanceof HTMLElement || attrs.appendTo instanceof ShadowRoot)) attrs.appendTo.append(el);
        else if (key === 'prependTo' && (attrs.prependTo instanceof HTMLElement || attrs.prependTo instanceof ShadowRoot)) attrs.prependTo.prepend(el);
        else if (key === 'insertBefore' && attrs.insertBefore instanceof HTMLElement) attrs.insertBefore.before(el);
        else if (key === 'insertAfter' && attrs.insertAfter instanceof HTMLElement) attrs.insertAfter.after(el);
        else if (key.startsWith("on")) el.addEventListener(key.substring(2), attrs[key] as unknown as EventListener, false);
		else if (key.startsWith(":")) el[key.substring(1)] = attrs[key];
		else if (key === 'checked' && 'checked' in el) el.checked = attrs.checked;
		else if (key === 'selected' && 'selected' in el) el.selected = attrs.selected;
		else if (key === 'multiple' && 'multiple' in el) el.multiple = attrs.multiple;
		else el.setAttribute(key, attrs[key]);
	
	for(var i=0;i<children.length;i++){
		if ((children[i] as any)?.rootElement instanceof HTMLElement) el.appendChild((<UIComponent>children[i]).rootElement);
		else if (children[i] instanceof HTMLElement) el.appendChild(<HTMLElement>children[i]);
		else if (children[i]) el.appendChild(preprocessText(""+children[i]));
	}
	return el;
}
export const DIV = EL.bind(null, 'div') as (attrs:any, ...children:(string|HTMLElement)[]) => HTMLDivElement;
export function hide(el: HTMLElement) {
    el.setAttribute('hidden', '');
}
export function show(el: HTMLElement, show: boolean = true) {
    if (show) {
        el.removeAttribute('hidden');
        if (el.style.display === 'none') el.style.display = '';
    } else {
        el.setAttribute('hidden', '');
    }
}
export function isVisible(el: HTMLElement) {
    return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
}
export function preprocessText(str: string): DocumentFragment {
    const parts = str.split('\u000F');
    const out = new DocumentFragment();
    for(let i = 0; i < parts.length - 1; i++) {
        const [plain, ctrl, ...args] = parts[i].split('\u000E');
        out.append(plain);
        try {
            out.append(preprocessText.ctrl[ctrl](...args));
        } catch(e) {
            out.append(EL('abbr', {style:{color:'red',padding:'0 3px;'},title:'Error: '+e+' (args: '+args+')'}, ''+ctrl))
        }
    }
    out.append(parts[parts.length - 1]);
    return out;
}
preprocessText.ctrl = {
    'ICO': function(icon) {
        const ico = renderEmoji(icon, UiContext.instance.db);
        if (ico instanceof HTMLImageElement) ico.alt = `\u000EICO\u000E${icon}\u000F`;
        return ico;
    }
}
export function mkCtrlSeq(ctrl: string, ...args: string[]): string {
    return `\u000E${[ctrl, ...args].join('\u000E')}\u000F`;
}
export function renderEmoji(icon: string | undefined, db: IBlobStore, scaleLevel: number = 1.4) {
    if (icon) {
        const style= {height:'1em',width:'1em',transform:`scale(${scaleLevel*100}%)`,padding:`0 ${Math.max(0, (scaleLevel-1)/2)}em`,boxSizing:'content-box',verticalAlign:'middle'};
        if (/^[a-zA-Z0-9_.-]+\.png$/.test(icon)) {
            return EL("img", {src:'./icons/FatCow_Icons32x32/' + icon,srcset:`./icons/FatCow_Icons16x16/${icon} 16w, ./icons/FatCow_Icons16x16/${icon} 32w`,
                style,class:'emoji-c emoji-img'});
        } else if (/^[a-zA-Z0-9_.-]+\.svg$/.test(icon)) {
            return EL("img", {src:'./icons/svg/' + icon, style,class:'emoji-c emoji-img'});
        } else if (/^[a-f0-9]{64}$/.test(icon) || /^(image\/svg\+xml:)?[a-zA-Z0-9_-]{86}$/.test(icon)) {
            const img = EL('img', { style,class:'emoji-c emoji-img'});
            if (db) db.getMediaURL(icon as string).then((url) => img.src = url);
            return img;
        } else {
            return EL('span', {class:'emoji-c'}, icon);
        }
    } else {
        return undefined;
    }
}
export function resolveCustomEmoji(icon: string | undefined, db: IBlobStore): Promise<string> {
    if (/^[a-zA-Z0-9_.-]+\.png$/.test(icon)) {
        return Promise.resolve('./icons/FatCow_Icons32x32/' + icon);
    } else if (/^[a-zA-Z0-9_.-]+\.svg$/.test(icon)) {
        return Promise.resolve('./icons/svg/' + icon);
    } else if (/^[a-f0-9]{64}$/.test(icon) || /^(image\/svg\+xml:)?[a-zA-Z0-9_-]{86}$/.test(icon)) {
        return db.getMediaURL(icon);
    } else {
        throw new Error("Invalid custom emoji spec "+icon);
    }
}
export function renderIcon2(icon: string | undefined, iconSize: number, db: IBlobStore, target: HTMLElement) {
    if (icon) {
        if (/^[a-zA-Z0-9_.-]+\.png$/.test(icon)) {
            if (iconSize >= 32) {
                target.replaceChildren(EL("img", {src:'./icons/FatCow_Icons32x32/' + icon}));
                return 32;
            } else {
                target.replaceChildren(EL("img", {src:'./icons/FatCow_Icons16x16/' + icon, style: {padding:'2px'}}));
                return 16;
            }
        } else if (/^[a-zA-Z0-9_.-]+\.svg$/.test(icon)) {
            target.replaceChildren(EL("img", {src:'./icons/svg/' + icon, style: {width: iconSize+"px",height: iconSize+"px"}}));
            return iconSize;
        } else if (/^[a-f0-9]{64}$/.test(icon) || /^(image\/svg\+xml:)?[a-zA-Z0-9_-]{86}$/.test(icon)) {
            if (db) db.getMediaURL(icon as string).then(
                (url) => target && (target.replaceChildren(EL("img", {src: url, style:{height: iconSize+"px"}}))));
            return iconSize;
        } else {
            target.innerText = icon; //`<span class='emoji emoji-${size}'>${icon}</span>`;
            target.style.fontSize = (iconSize*0.85) + "px";
            target.style.lineHeight = '1'
            return iconSize;
        }
    } else {
        target.innerHTML = "";
        return 0;
    }
}
export function effectAllowed(allowMove, allowLink) {
    if (allowMove && allowLink) return 'all';
    else if (allowMove) return 'copyMove';
    else if (allowLink) return 'copyLink';
    else return 'copy';
}
export async function bufferToBase64 (buffer : Uint8Array | ArrayBuffer): Promise<string> {
    const base64url = await new Promise<string>(r => {
        const reader = new FileReader()
        reader.onload = () => r(reader.result as string)
        reader.readAsDataURL(new Blob([buffer]))
    });
    return base64url.slice(base64url.indexOf(',') + 1).replace(/\//g, '_').replace(/\+/g, '-').replace(/=+$/, '');
}
export function selectDomElement(el : Node) {
    var selection = window.getSelection();
    var range = document.createRange();
    range.selectNodeContents(el);
    selection.removeAllRanges();
    selection.addRange(range);
}

if (!('isConnected' in Node.prototype)) {  // polyfill
  Object.defineProperty(Node.prototype, 'isConnected', {
    get() {
      return (
        !this.ownerDocument ||
        !(
          this.ownerDocument.compareDocumentPosition(this) &
          this.DOCUMENT_POSITION_DISCONNECTED
        )
      );
    },
  });
}

export function mkStyleSheet(text: string): CSSStyleSheet {
    const sty = new CSSStyleSheet();
    sty.replaceSync(text);
    return sty;
}

//miau obviously stands for "mira's interactive app utils"
if (window.customElements) {
    window.customElements.define('miau-icon', class extends HTMLElement {
        constructor() {
            super();
        }

        connectedCallback() {
            renderIcon2(this.getAttribute('src'), Math.max(8, +this.getAttribute('size') || 32), UiContext.instance.db, this);
        }
    });
    window.customElements.define('miau-emoji', class extends HTMLElement {
        constructor() {
            super();
        }

        connectedCallback() {
            this.append(renderEmoji(this.getAttribute('src'), UiContext.instance.db));
        }
    });

    const loadIndicatorCSS = new CSSStyleSheet();
    loadIndicatorCSS.replaceSync(`
        :host,
        :host div {
            box-sizing: border-box;
        }
        :host {
            display: inline-block;
            position: relative;
            width: 80px;
            height: 80px;
        }
        :host div {
            display: inline-block;
            position: absolute;
            left: 10%;
            width: 20%;
            animation: loadindicator 1.2s cubic-bezier(0, 0.5, 0.5, 1) infinite;
        }
        :host div:nth-child(1) {
            left: 10%;
            animation-delay: -0.24s;
            background: #e72c9c;
        }
        :host div:nth-child(2) {
            left: 40%;
            animation-delay: -0.12s;
            background: #bf54f1;
        }
        :host div:nth-child(3) {
            left: 70%;
            animation-delay: 0s;
            background: #5a54f1;
        }
        @keyframes loadindicator {
            0% {
                top: 10%;
                height: 80%;
            }
            50%, 100% {
                top: 25%;
                height: 50%;
            }
        }
        :host([small]) { width: 32px; height: 32px; }
        @keyframes loadindicator-small {
            0% {
                top: 5%;
                height: 90%;
            }
            50%, 100% {
                top: 30%;
                height: 40%;
            }
        }
        :host([center]) { margin: -40px auto 0; top: 50%; }
        :host([inline]) { display: inline-block; vertical-align: middle; margin: 0 10px; }
        :host([xs]) { width: 22px; height: 22px; }
        `);
    window.customElements.define('load-indicator', class extends HTMLElement {
        constructor() {
            super();
        }

        connectedCallback() {
            const root = this.attachShadow({ mode: 'open' });
            root.append(EL('div', {}), EL('div', {}), EL('div', {}));
            root.adoptedStyleSheets = [loadIndicatorCSS];
        }
    });
}

export function dragdrop1d(el, axis, areas: {
    button: number,
    condition: (offset: number, size: number) => boolean,
    init: (pos: number) => number,
    transform: (newval: number, diff: number, pointerpos: number) => void,
    finishMove?: (newval: number, diff: number, pointerpos: number) => void,
    startMove?: () => void,
    abortMove?: (initpos: number) => void,
    endMove?: () => void,
    tap?: (event: MouseEvent) => void,
    doubletap?: (event: MouseEvent) => void,
    allowDefault?: boolean,
    otherAxisCancelRatio?: number,
    lockThreshold?: number,
    dontMoveBeforeLock?: boolean,
}[]) {
    let diff = null, start = null, lastClick = 0, area = null, startOther = null, locked = false, moved = false;
    const Z = {x: 'X', y: 'Y'}[axis], ZZ = {x: 'Width', y: 'Height'}[axis];
    const clientZ = `client${Z}`, offsetZ = `offset${Z}`, offsetZZ = `offset${ZZ}`;
    const clientZOther = `client${{x: 'Y', y: 'X'}[axis]}`;
    if (!el.style.touchAction) el.style.touchAction = `pan-${{x: 'y', y: 'x'}[axis]} pinch-zoom` ;
    el.addEventListener('pointerdown', (e) => {
        for(const a of areas) {
            if (e.buttons === a.button && a.condition.call(el, e[offsetZ], el[offsetZZ], e[clientZ], e[clientZOther])) {
                start = e[clientZ]; startOther = e[clientZOther]; locked = false; moved = false;
                diff = e[clientZ] - a.init.call(el, e[clientZ]);
                if (area !== a) lastClick = 0;
                area = a;

                if (!area.allowDefault) { e.preventDefault(); e.stopPropagation(); }
                return;
            }
        }
    }, {});
    el.addEventListener('pointermove', (e) => {
        if (area && e.buttons == area.button && diff != null) {
            if (!moved) { el.setPointerCapture(e.pointerId); area.startMove?.call(el); }
            if (!locked && Math.abs(e[clientZ] - start) > (area.lockThreshold || 5)) { locked = true; }
            if (!locked && area.otherAxisCancelRatio && Math.abs(e[clientZOther] - startOther) > (area.otherAxisCancelRatio * Math.abs(e[clientZ] - start))+3) {
                area.transform.call(el, start - diff, 0);
                area.endMove?.call?.(el);
                area.abortMove?.call?.(el, start - diff);
                start = null; diff = null;
                el.releasePointerCapture(e.pointerId);
                return;
            }
            if (locked || !area.dontMoveBeforeLock) area.transform.call(el, e[clientZ] - diff, e[clientZ] - start, e[clientZ]);
            moved = true;
            if (!area.allowDefault || locked) { e.preventDefault(); e.stopPropagation(); }
        }
    }, {});
    el.addEventListener('pointerup', (e) => {
        if (area && diff != null) {
            if (locked) {
                e.preventDefault(); e.stopPropagation();
                area.transform.call(el, e[clientZ] - diff, e[clientZ] - start, e[clientZ]);
                area.endMove?.call?.(el);
                area.finishMove?.call?.(el, e[clientZ] - diff, e[clientZ] - start, e[clientZ]);
            } else {
                if (moved) {
                    area.transform.call(el, start - diff, 0);
                    area.endMove?.call?.(el);
                    area.abortMove?.call?.(el, start - diff);
                }

                if (!secondsElapsedSince(0.5, lastClick)) { //double click action
                    if (area.doubletap) {
                        e.preventDefault();
                        e.stopPropagation();
                        area.doubletap(e);
                    }
                } else {
                    if (area.tap) {
                        e.preventDefault();
                        e.stopPropagation();
                        area.tap(e);
                    }
                }
                lastClick = now();
            }
            start = null; diff = null;
        }
        el.releasePointerCapture(e.pointerId);
    }, {});
    el.addEventListener('pointercancel', (e) => {
        if (area && diff != null) {
            if (moved) {
                area.transform.call(el, start - diff, 0);
                area.endMove?.call?.(el);
                area.abortMove?.call?.(el, start - diff);
            }
            start = null; diff = null;
        }
        el.releasePointerCapture(e.pointerId);
    }, {});
}
